import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { BackgroundTaskService, BackgroundTask } from '../../../shared/services/background-task.service';

@Component({
    selector: 'app-background-task-toast',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './background-task-toast.component.html',
    styleUrls: ['./background-task-toast.component.css']
})
export class BackgroundTaskToastComponent implements OnInit, OnDestroy {
    tasks: BackgroundTask[] = [];
    private subscription?: Subscription;

    // Drag and drop state
    isDragging = false;
    dragOffset = { x: 0, y: 0 };
    position = { x: 0, y: 0 };
    hasCustomPosition = false;

    @ViewChild('toastContainer') toastContainerRef!: ElementRef;

    constructor(
        private readonly backgroundTaskService: BackgroundTaskService,
        private readonly router: Router
    ) { }

    ngOnInit(): void {
        this.subscription = this.backgroundTaskService.tasks$.subscribe(tasks => {
            this.tasks = tasks;
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    navigateToPage(task: BackgroundTask): void {
        if (task.returnRoute) {
            this.router.navigate([task.returnRoute]);
        }
    }

    dismissTask(taskId: string): void {
        this.backgroundTaskService.removeTask(taskId);
    }

    // Drag and drop methods
    onDragStart(event: MouseEvent): void {
        // Only allow dragging from the header area
        const target = event.target as HTMLElement;
        if (!target.closest('.toast-header') || target.closest('button')) {
            return;
        }

        this.isDragging = true;
        const container = this.toastContainerRef?.nativeElement;
        if (container) {
            const rect = container.getBoundingClientRect();
            this.dragOffset = {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top
            };
            if (!this.hasCustomPosition) {
                this.position = { x: rect.left, y: rect.top };
                this.hasCustomPosition = true;
            }
        }
        event.preventDefault();
    }

    @HostListener('document:mousemove', ['$event'])
    onDragMove(event: MouseEvent): void {
        if (!this.isDragging) return;

        const newX = event.clientX - this.dragOffset.x;
        const newY = event.clientY - this.dragOffset.y;

        // Get actual container dimensions
        const container = this.toastContainerRef?.nativeElement;
        const containerWidth = container ? container.offsetWidth : 380;
        const containerHeight = container ? container.offsetHeight : 100;

        // Constrain to viewport with consistent margin (same as CSS initial position)
        const margin = 20;
        const maxX = window.innerWidth - containerWidth - margin;
        const maxY = window.innerHeight - containerHeight - margin;

        this.position = {
            x: Math.max(margin, Math.min(newX, maxX)),
            y: Math.max(margin, Math.min(newY, maxY))
        };
    }

    @HostListener('document:mouseup')
    onDragEnd(): void {
        this.isDragging = false;
    }

    getContainerStyle(): { [key: string]: string } | null {
        if (!this.hasCustomPosition) return null;
        return {
            left: `${this.position.x}px`,
            top: `${this.position.y}px`,
            bottom: 'auto',
            right: 'auto'
        };
    }
}
